import { useState, useEffect, useCallback } from "react";
import { Typography, Button, Table, Space, Input, Select, Tooltip } from "antd";
import {
  PlusOutlined, FileTextOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  EyeOutlined, ReloadOutlined, FileExcelOutlined, UploadOutlined,
} from "@ant-design/icons";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import StatCard, { StatGrid } from "../components/StatCard";
import StatusTag from "../components/StatusTag";
import ClaimDetailDrawer from "./ClaimDetailDrawer";
import SubmitClaimDrawer from "./SubmitClaimDrawer";
import BulkUploadDrawer from "./BulkUploadDrawer";
import { CorpClaimPortalService, CORP_CLAIM_STATUS } from "../services/corpClaimPortalService";
import { useAuth } from "../context/AuthContext";
import { notifyUser } from "../utils/notify";

const { Title, Text } = Typography;

const Dashboard = () => {
  const { user } = useAuth();
  const { claimId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState();
  const [exporting, setExporting] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailClaim, setDetailClaim] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editClaim, setEditClaim] = useState(null);
  const [claimTypeLabels, setClaimTypeLabels] = useState({});

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await CorpClaimPortalService.stats();
      setStats(res.data);
    } catch {
      notifyUser("error", "Could not load your claim summary.", "Error");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const load = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await CorpClaimPortalService.list({
          status: statusFilter,
          search: search.trim() || undefined,
          page,
          per_page: pagination.pageSize,
        });
        const paged = res?.data || {};
        setRows(paged.data || []);
        setPagination((p) => ({ ...p, current: paged.current_page || 1, total: paged.total || 0 }));
      } catch {
        notifyUser("error", "Could not load your claims.", "Error");
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, search, pagination.pageSize],
  );

  useEffect(() => {
    loadStats();
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Claims store the raw SLAMS claim-type code; resolved once here so the
  // table/drawer can show the human label instead.
  useEffect(() => {
    (async () => {
      try {
        const res = await CorpClaimPortalService.referenceData("claim_type");
        const map = {};
        (res?.data || []).forEach((t) => { map[t.code] = t.label; });
        setClaimTypeLabels(map);
      } catch {
        // Table just falls back to showing the raw code.
      }
    })();
  }, []);

  const openDetail = async (id) => {
    setDetailOpen(true);
    try {
      const res = await CorpClaimPortalService.get(id);
      setDetailClaim(res.data);
    } catch {
      notifyUser("error", "Could not open that claim.", "Error");
      setDetailOpen(false);
    }
  };

  // Deep-link support: /claims/:claimId opens straight into the drawer.
  useEffect(() => {
    if (claimId) openDetail(claimId);
  }, [claimId]);

  // The old nav-link "New Claim" / other pages navigate here with this flag.
  useEffect(() => {
    if (location.state?.openSubmit) {
      setSubmitOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const refresh = () => { load(pagination.current); loadStats(); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await CorpClaimPortalService.export({ status: statusFilter });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `My_Corporate_Claims_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notifyUser("error", "Could not export your claims.", "Error");
    } finally {
      setExporting(false);
    }
  };

  const handleSubmitted = (claimIdResult) => {
    setSubmitOpen(false);
    setEditClaim(null);
    refresh();
    openDetail(claimIdResult);
  };

  const handleEdit = (claim) => {
    setDetailOpen(false);
    setEditClaim(claim);
    setSubmitOpen(true);
  };

  const columns = [
    {
      title: "Claim",
      dataIndex: "id",
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>CC-{String(v).padStart(6, "0")}</Text>
          <Text type="secondary" style={{ fontSize: 12.5 }}>{r.claimant_name}</Text>
        </Space>
      ),
    },
    { title: "Type", dataIndex: "claim_type_code", render: (v) => claimTypeLabels[v] || v },
    {
      title: "Scheme",
      dataIndex: "scheme_no",
      render: (v, r) => {
        const name = r.slams_member_snapshot?.scheme_information?.scheme_name?.trim();
        return (
          <Space direction="vertical" size={0}>
            {name && <Text style={{ fontSize: 13 }}>{name}</Text>}
            <Text type="secondary" style={{ fontSize: name ? 12 : 13 }}>{v}</Text>
          </Space>
        );
      },
    },
    { title: "Submitted", dataIndex: "submitted_at", render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : "—") },
    { title: "Documents", dataIndex: "documents_count", align: "center" },
    { title: "Status", dataIndex: "status", render: (v) => <StatusTag status={v} /> },
    {
      title: "",
      key: "actions",
      align: "center",
      render: (_, r) => (
        <Space size={4}>
          {r.status === "MISSING_DOCUMENTS" && (
            <Tooltip title="Upload the requested documents">
              <Button
                size="small"
                danger
                icon={<UploadOutlined />}
                onClick={() => openDetail(r.id)}
              >
                Upload
              </Button>
            </Tooltip>
          )}
          <Tooltip title="View Details">
            <Button
              shape="circle"
              size="small"
              icon={<EyeOutlined />}
              style={{ borderColor: "#189F6B", color: "#189F6B" }}
              onClick={() => openDetail(r.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="py-4">
      <div className="row align-items-center mb-4">
        <div className="col">
          <Title level={4} style={{ marginBottom: 0 }}>Welcome, {user?.name}</Title>
          <Text type="secondary">{user?.organisation} &middot; {user?.role}</Text>
        </div>
        <div className="col-auto">
          <Space>
            <Button size="large" icon={<UploadOutlined />} onClick={() => setBulkOpen(true)}>
              Bulk Upload
            </Button>
            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setSubmitOpen(true)}>
              Submit a Claim
            </Button>
          </Space>
        </div>
      </div>

      <StatGrid className="mb-4">
        <StatCard label="Total claims submitted" value={stats?.total ?? 0} icon={<FileTextOutlined />} accent="#1677ff" loading={statsLoading} />
        <StatCard label="In progress" value={stats?.pending ?? 0} icon={<ClockCircleOutlined />} accent="#d48806" loading={statsLoading} />
        <StatCard label="Approved / paid" value={stats?.approved ?? 0} icon={<CheckCircleOutlined />} accent="#45AE55" loading={statsLoading} />
        <StatCard label="Rejected" value={stats?.rejected ?? 0} icon={<CloseCircleOutlined />} accent="#cf1322" loading={statsLoading} />
      </StatGrid>

      <div className="bg-white rounded shadow-sm p-4">
        <div className="row align-items-center mb-3">
          <div className="col"><Title level={5} style={{ marginBottom: 0 }}>My Claims</Title></div>
        </div>

        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            allowClear
            placeholder="Claimant, scheme or staff no."
            style={{ width: 280 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={() => load(1)}
          />
          <Select
            allowClear
            placeholder="All statuses"
            style={{ width: 200 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.entries(CORP_CLAIM_STATUS).map(([value, s]) => ({ value, label: s.label }))}
          />
          <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
          <Button icon={<FileExcelOutlined />} loading={exporting} onClick={handleExport}>Export</Button>
        </Space>

        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          loading={loading}
          pagination={{ ...pagination, showSizeChanger: false, showTotal: (t) => `${t} claim${t === 1 ? "" : "s"}` }}
          onChange={(p) => load(p.current)}
          locale={{ emptyText: "You have not submitted any claims yet." }}
          scroll={{ x: 900 }}
        />
      </div>

      <ClaimDetailDrawer
        open={detailOpen}
        claim={detailClaim}
        claimTypeLabels={claimTypeLabels}
        onClose={() => setDetailOpen(false)}
        onChanged={() => openDetail(detailClaim.id)}
        onWithdrawn={refresh}
        onEdit={handleEdit}
      />

      <SubmitClaimDrawer
        open={submitOpen}
        editClaim={editClaim}
        onClose={() => { setSubmitOpen(false); setEditClaim(null); }}
        onSubmitted={handleSubmitted}
      />

      <BulkUploadDrawer
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSubmitted={refresh}
      />
    </div>
  );
};

export default Dashboard;
